import { Button, Card, CardContent, FormControl, FormControlLabel, Grid, IconButton, Radio, RadioGroup, Skeleton, Tooltip, Typography } from "@mui/material";
import { Dialog as KendoDialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import DeleteIcon from '@mui/icons-material/Delete';

import { useSaveData } from "../../../hooks/useSaveData";
import { useLoadData } from "../../../hooks/useLoadData";
import { ApiResponse } from "../../../models/api-response";
import FormInput from "../../../components/hook-form/form-input.component";
import { planReviewApiKeyStore } from "../../../api/plan-review-api-key-store";
import { LoadingIndicator } from "../../../components/loading-indicator.component";
import { GuardedComponent } from "../../../components/guarded-component";
import { UserPermissionName } from "../../../models/user-permission-name";
import { Icons } from "../../../assets/icons";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useFileDownload } from "../../../components/file-download";
import { ApiErrorComponent } from "../../../components/api-error.component";
import { useDeleteData } from "../../../hooks/useDeleteData";
import { unitsApiKeyStore } from "../../../api/units-api-key-store";
import { useFormContext } from "react-hook-form";
import { useAuthorize } from "../../../hooks/use-authorize";
import { FormSelect } from "../../query-builder/hooks-form-querybuilder/form-select.component";
import { FormCheckbox } from "../../../components/hook-form/form-checkbox.component";

const styling = {
    dropZone: {
        padding: '10px 5px 10px',
        border: '0.1em dashed #99A3A4',
        backgroundColor: '#F2F3F4',
        // display: 'none',
        justifyContent: 'center',
        cursor: 'pointer',
        borderRadius: '4px',
        marginRight: '0px',
    },
}

const options = [
    {
        value: "Cooling",
        label: "Cooling",
        isDefault: false,
    },
    {
        value: "Heating",
        label: "Heating",
        isDefault: false,
    },
];

interface PlanReviewTaskDialog {
    title: string
    planReviewData: any;
    moduleCode: string;
    isParentLoading: boolean;
    handleSubmitData: (statusData: any) => void;
    parentRefetch: () => void;
    passFileData: (fileData: any) => void;
    passAccFileData: (fileData: any) => void;
    passStatusValue: (status: string) => void;
}

export const PlanReviewTaskResult = ({ title, planReviewData, moduleCode, isParentLoading, handleSubmitData, parentRefetch, passFileData, passAccFileData, passStatusValue }: PlanReviewTaskDialog) => {

    const { getValues } = useFormContext();
    const [file, setFile] = useState<any>();
    const [accFile, setAccFile] = useState<any>();
    const [openDeleteDialog, setDeleteDialog] = useState<boolean>(false);
    const [openAccDeleteDialog, setAccDeleteDialog] = useState<boolean>(false);

    const {
        error,
        isError,
        isLoading: fileDeleteLoading,
        mutateAsync: mutateDeleteFile,
    } = useDeleteData(unitsApiKeyStore.deleteUnitFile(planReviewData?.unitID),
        () => planReviewApiKeyStore.getPlanReviewTask(planReviewData?.planReviewTaskID));


    const {
        error: accReportError,
        isError: isAccReportError,
        isLoading: accReportDeleteLoading,
        mutateAsync: mutateAccDeleteReport,
    } = useDeleteData(unitsApiKeyStore.deleteUnitAccReportFile(planReviewData?.unitID),
        () => planReviewApiKeyStore.getPlanReviewTask(planReviewData?.planReviewTaskID));

    const { downloadFile, isLoading: isFileDownloadLoading } = useFileDownload();

    const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any>>(
        planReviewApiKeyStore.getPlanReviewStatus(moduleCode, planReviewData?.planReviewTaskID)
    );

    const { isLoading: isTaskStatusLoading, mutateAsync } = useSaveData<any>(planReviewApiKeyStore.updatePlanReviewTaskStatus(),
        () => planReviewApiKeyStore.getPlanReviewTask(planReviewData?.planReviewTaskID));

    const handleStatusClick = async (item: any) => {
        const updateStatus = {
            planReviewTaskID: planReviewData?.planReviewTaskID,
            planReviewTaskStatusCode: item?.planReviewTaskStatusCode
        }
        if (moduleCode == 'PDB_ENERGY' || moduleCode == 'PDB_HVAC' || moduleCode == 'PDB_FILE')
            handleSubmitData(updateStatus);
        else {
            await mutateAsync({ data: updateStatus });
            parentRefetch();
            refetch();
        }
    };

    const handleDownloadFile = async (data: any) => {
        const value = {
            unitID: planReviewData?.unitID,
            fileName: data?.hvacDesignFileName,
            fileType: data?.hvacDesignFileType
        };
        downloadFile({ additionalData: value });
    };

    const handleDrop = useCallback((acceptedFiles: any) => {
        setFile(acceptedFiles[0]);
        passFileData(acceptedFiles[0])
    }, []);

    const handleDeleteFileClick = async () => {
        if (planReviewData?.hvacDesignFileName) {
            const deleteData = await mutateDeleteFile();
        }
        setFile(undefined);
        passFileData(undefined)
        handleClosedDeleteDialog();
    };

    const handleOpenDeleteDialog = () => {
        setDeleteDialog(true);
    };

    const handleClosedDeleteDialog = () => {
        setDeleteDialog(false);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDrop,
    });

    const handleAccDrop = useCallback((acceptedFiles: any) => {
        setAccFile(acceptedFiles[0]);
        passAccFileData(acceptedFiles[0])
    }, []);

    const handleDownloadAccFile = async (data: any) => {
        const value = {
            unitID: planReviewData?.unitID,
            fileName: data?.accaReportFileName,
            fileType: data?.accaReportFileType,
            fileSection: "accafile"
        };
        downloadFile({ additionalData: value });
    };

    const handleAccDeleteFileClick = async () => {
        if (planReviewData?.accaReportFileName) {
            const deleteData = await mutateAccDeleteReport();
        }
        setAccFile(undefined);
        passAccFileData(undefined)
        handleClosedAccDeleteDialog();
    };

    const handleOpenAccDeleteDialog = () => {
        setAccDeleteDialog(true);
    };

    const handleClosedAccDeleteDialog = () => {
        setAccDeleteDialog(false);
    };

    const { getRootProps: getAccRootProps, getInputProps: getAccInputProps, isDragActive: isAccDragActive } = useDropzone({
        onDrop: handleAccDrop,
    });

    const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        passStatusValue((event.target as HTMLInputElement).value);
    };

    const [isHVACGrading, setHVACGradingCheckbox] = useState<boolean>(false);

    useEffect(() => {
        setHVACGradingCheckbox(planReviewData?.includeHVACGrading ?? false)
    }, [planReviewData?.includeHVACGrading])

    const handleHVACGradingCheckbox = (event: any) => {
        setHVACGradingCheckbox(event?.target?.checked);
        // setAccFile(undefined);
        // setUnitAccaReportFileName("");
        // form.setValue('designedAHModel', "");
        // form.setValue('accaReportFileName', "");
        // form.setValue('accaReportFileType', "");
        // form.setValue('blowerFanAirflowDesign', "");
        // form.setValue('blowerFanAirflowDesignSetting', "");
        // form.setValue('accaReportFile', "");

        // form.setValue('sys1AirFlowResult', "");
        // form.setValue('sys1AirFlowScoring', "");
        // form.setValue('sys1AirFlowGrading', "");
        // form.setValue('sys2AirFlowResult', "");
        // form.setValue('sys2AirFlowScoring', "");
        // form.setValue('sys2AirFlowGrading', "");

        // form.setValue('sys1DesignedAHModel', "");
        // form.setValue('sys1BlowerFanAirflowDesign', "");
        // form.setValue('sys1BlowerFanAirflowDesignSetting', "");
    }

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

    return (
        <>
            <Grid container spacing={2}>
                <Grid className="subHead" item lg={12} sm={12} xl={12} xs={12}>
                    <Typography variant="h3" color="textPrimary">
                        {title?.toUpperCase()} RESULTS
                    </Typography>
                </Grid>
                {(moduleCode !== "PDB_PPI" && moduleCode !== "PDB_FILE") && (
                    <Grid item lg={12} sm={12} xl={12} xs={12}>
                        <Grid container spacing={2}>
                            <Grid item lg={3} sm={3} xl={3} xs={3}>
                                <FormInput
                                    name="conditionedArea"
                                    label="Conditioned Area (ACH Area)"
                                    type="number"
                                />
                            </Grid>
                            <Grid item lg={3} sm={3} xl={3} xs={3}>
                                <FormInput
                                    name="volume"
                                    label="Volume (cu. ft.)"
                                    type="number"
                                />
                            </Grid>
                            <Grid item lg={3} sm={3} xl={3} xs={3}>
                                <FormInput
                                    name="windowArea"
                                    label="Window Area"
                                    type="number"
                                />
                            </Grid>
                            {/* {moduleCode == "PDB_ENERGY" && (
                                <Grid item lg={3} sm={3} xl={3} xs={3}>
                                    <FormInput
                                        name="squareFeet"
                                        label="Square Feet"
                                        type="number"
                                    />
                                </Grid>
                            )} */}
                        </Grid>
                    </Grid>
                )}
                {moduleCode == "PDB_FILE" && (
                    <Grid item lg={12} sm={12} xl={12} xs={12}>
                        <Grid container spacing={2}>
                            <Grid item lg={3} sm={3} xl={3} xs={3}>
                                <FormInput
                                    name="registrationNumber"
                                    label="Registration Number"
                                />
                            </Grid>
                            <Grid item lg={3} sm={3} xl={3} xs={3}>
                                <FormInput
                                    name="hersScore"
                                    label="HERS Score"
                                    type="number"
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                )}
                {/* <Grid item lg={9} sm={9} xl={9} xs={9}>
                    <FormInput name="note" label="Notes" multiline size={3} />
                </Grid> */}
                {moduleCode == "PDB_HVAC" && (
                    <Grid item lg={12} sm={12} xl={12} xs={12}>
                        <Grid container spacing={1}>
                            <Grid item lg={3} sm={3} xl={3} xs={3}>
                                <FormInput
                                    name="airflow"
                                    label="Airflow"
                                    type="number"
                                />
                            </Grid>
                            <Grid item lg={3} sm={3} xl={3} xs={3}>
                                <FormInput
                                    name="hvacContractor"
                                    label="HVAC Contractor"
                                />
                            </Grid>
                            <Grid item lg={3} sm={3} xl={3} xs={3}>
                                <LoadingIndicator isLoading={isParentLoading} />
                                <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
                                    {!file && !planReviewData?.hvacDesignFileName && (
                                        <div className="inlineUploadwithLabel" style={styling.dropZone} {...getRootProps()}>
                                            <label className="label">HVAC Design File</label>
                                            <input {...getInputProps()} />
                                            {isDragActive ? (
                                                <p>Drop the files here ...</p>
                                            ) : (
                                                <div>
                                                    <p>Drag and drop a file here, or click to select a file</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </GuardedComponent>
                                {(file || planReviewData?.hvacDesignFileName) && (
                                    <div className="inlineDownloadwithLabel">
                                        <div>
                                            <label className="label">HVAC Design File</label>
                                            <label className="fileName"> {!file ? planReviewData?.hvacDesignFileName ?? '' : file?.name}</label>
                                        </div>
                                        <div>
                                            <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
                                                {!!planReviewData?.hvacDesignFileName && (
                                                    <span>
                                                        <IconButton aria-label="download" onClick={() => handleDownloadFile(planReviewData)}>
                                                            <img src={Icons.FileDownload} alt="Download" />
                                                        </IconButton>
                                                    </span>
                                                )}
                                            </GuardedComponent>
                                            <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_FILE]}>
                                                <span>
                                                    <IconButton aria-label="delete" onClick={handleOpenDeleteDialog}>
                                                        <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                                            <DeleteIcon className="btnDelete" />
                                                        </Tooltip>
                                                    </IconButton>
                                                </span>
                                            </GuardedComponent>
                                        </div>
                                    </div>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                )}
                {moduleCode == "PDB_HVAC" && (
                    <Grid item lg={9} sm={9} xl={9} xs={9}>
                        <Card elevation={0} style={{ borderStyle: 'solid', borderWidth: '2px', borderColor: '#A6A09B' }}>
                            <CardContent>
                                <Grid container spacing={1}>
                                    <Grid item lg={2} sm={2} xl={2} xs={2}>
                                        <FormCheckbox
                                            label="HVAC Grading"
                                            name="includeHVACGrading"
                                            onChange={handleHVACGradingCheckbox}
                                        />
                                        {isHVACGrading && (
                                            <FormCheckbox
                                                label="Weigh in data received"
                                                name="weighInDataReceived"
                                            />
                                        )}
                                    </Grid>
                                    {isHVACGrading && (
                                        <Grid item lg={10} sm={10} xl={10} xs={10}>
                                            <Grid container spacing={2}>
                                                <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormInput
                                                                name="sys1DesignedAHModel"
                                                                label="System 1 Design AH Model"
                                                            />
                                                        </Grid>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormInput
                                                                name="sys1BlowerFanAirflowDesign"
                                                                label="System 1 Blower Fan Airflow Design"
                                                                type="number"
                                                            />
                                                        </Grid>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormSelect
                                                                label="System 1 Blower Fan Airflow Design Setting"
                                                                name="sys1BlowerFanAirflowDesignSetting"
                                                                options={options} />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormInput
                                                                name="designedAHModel"
                                                                label="System 2 Designed AH Model"
                                                            />
                                                        </Grid>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormInput
                                                                name="blowerFanAirflowDesign"
                                                                label="System 2 Blower Fan Airflow Design"
                                                                type="number"
                                                            />
                                                        </Grid>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormSelect
                                                                label="System 2 Blower Fan Airflow Design Setting"
                                                                name="blowerFanAirflowDesignSetting"
                                                                options={options} />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid item lg={5} sm={5} xl={5} xs={5}>
                                                            <LoadingIndicator isLoading={isParentLoading} />
                                                            <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
                                                                {!accFile && !planReviewData?.accaReportFileName && (
                                                                    <div className="inlineUploadwithLabel" style={styling.dropZone} {...getAccRootProps()}>
                                                                        <label className="label">ACCA 310 Design Report</label>
                                                                        <input {...getAccInputProps()} />
                                                                        {isAccDragActive ? (
                                                                            <p>Drop the files here ...</p>
                                                                        ) : (
                                                                            <div>
                                                                                <p>Drag and drop a file here, or click to select a file</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </GuardedComponent>
                                                            {(accFile || planReviewData?.accaReportFileName) && (
                                                                <div className="inlineDownloadwithLabel">
                                                                    <div>
                                                                        <label className="label">ACCA 310 Design Report</label>
                                                                        <label className="fileName"> {!accFile ? planReviewData?.accaReportFileName ?? '' : accFile?.name}</label>
                                                                    </div>
                                                                    <div>
                                                                        <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
                                                                            {!!planReviewData?.accaReportFileName && (
                                                                                <span>
                                                                                    <IconButton aria-label="download" onClick={() => handleDownloadAccFile(planReviewData)}>
                                                                                        <img src={Icons.FileDownload} alt="Download" />
                                                                                    </IconButton>
                                                                                </span>
                                                                            )}
                                                                        </GuardedComponent>
                                                                        <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_FILE]}>
                                                                            <span>
                                                                                <IconButton aria-label="delete" onClick={handleOpenAccDeleteDialog}>
                                                                                    <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                                                                        <DeleteIcon className="btnDelete" />
                                                                                    </Tooltip>
                                                                                </IconButton>
                                                                            </span>
                                                                        </GuardedComponent>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormInput
                                                                name="sys1AirFlowResult"
                                                                label="System 1 Airflow Result"
                                                                type="number"
                                                                disabled
                                                            />
                                                        </Grid>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormInput
                                                                name="sys1AirFlowScoring"
                                                                label="System 1 Airflow Scoring"
                                                                type="number"
                                                                disabled
                                                            />
                                                        </Grid>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormInput
                                                                name="sys1AirFlowGrading"
                                                                label="System 1 Airflow Grading"
                                                                disabled
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormInput
                                                                name="sys2AirFlowResult"
                                                                label="System 2 Airflow Result"
                                                                type="number"
                                                                disabled
                                                            />
                                                        </Grid>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormInput
                                                                name="sys2AirFlowScoring"
                                                                label="System 2 Airflow Scoring"
                                                                type="number"
                                                                disabled
                                                            />
                                                        </Grid>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormInput
                                                                name="sys2AirFlowGrading"
                                                                label="System 2 Airflow Grading"
                                                                disabled
                                                            />
                                                        </Grid>
                                                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                                                            <FormInput
                                                                name="liqLineLength"
                                                                label="Liquid Line Length"
                                                                disabled
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                <Grid item lg={12} sm={12} xl={12} xs={12}>
                    <Grid container spacing={2}>
                        <Grid item lg={3} sm={3} xl={3} xs={3}>
                            <LoadingIndicator isLoading={isParentLoading} />
                            <FormInput
                                name="currentStatus"
                                label="Current Status"
                                disabled
                            />
                        </Grid>
                        {moduleCode == "PDB_PPI" && (
                            <Grid item lg={2} sm={2} xl={2} xs={2}>
                                <LoadingIndicator isLoading={isParentLoading} />
                                <FormInput
                                    name="revisionNumber"
                                    label="Version"
                                    disabled
                                />
                            </Grid>
                        )}
                        <Grid item
                            lg={moduleCode == "PDB_PPI" ? 7 : moduleCode == "PDB_HVAC" ? 6 : 9}
                            sm={moduleCode == "PDB_PPI" ? 7 : moduleCode == "PDB_HVAC" ? 6 : 9}
                            xl={moduleCode == "PDB_PPI" ? 7 : moduleCode == "PDB_HVAC" ? 6 : 9}
                            xs={moduleCode == "PDB_PPI" ? 7 : moduleCode == "PDB_HVAC" ? 6 : 9}>
                            <Grid
                                container
                                direction="row"
                                sx={{
                                    justifyContent: "flex-start",
                                    alignItems: "flex-start",
                                }}
                            >

                                {(isLoading || isFetching || isTaskStatusLoading) ? (
                                    <Skeleton variant="rectangular" width={210} height={40} />
                                ) :

                                    <FormControl>
                                        <RadioGroup
                                            row
                                            aria-labelledby="demo-row-radio-buttons-group-label"
                                            name="row-radio-buttons-group"
                                            onChange={handleStatusChange}
                                        >
                                            {data?.data?.map((item: any) => (
                                                (getValues('currentStatus') !== item?.planReviewTaskStatusName) &&
                                                <div key={item?.planReviewTaskStatusID}>
                                                    <FormControlLabel value={item?.planReviewTaskStatusCode} control={<Radio />} label={item?.planReviewTaskStatusName} />
                                                </div>
                                            ))}
                                        </RadioGroup>
                                    </FormControl>
                                    // (
                                    //     data?.data?.map((item: any) => (
                                    //         (getValues('currentStatus') !== item?.planReviewTaskStatusName) &&
                                    //         <div key={item?.planReviewTaskStatusID} style={{ paddingRight: 5 }}>
                                    //             <Button
                                    //                 style={{ width: item?.planReviewTaskStatusName?.length < 10 ? '130px' : '210px' }}
                                    //                 type='submit' variant="contained" onClick={() => handleStatusClick(item)}>
                                    //                 {item?.planReviewTaskStatusName}
                                    //             </Button>
                                    //         </div>
                                    //     ))
                                    // )
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid >
            {openDeleteDialog && (
                <KendoDialog className="modalpopup" title={'CONFIRM DELETE'} onClose={handleClosedDeleteDialog}>
                    <div>
                        <div style={{ padding: '25px 50px' }}>
                            <div>Are you sure you want to permanently delete?</div>
                            <LoadingIndicator isLoading={fileDeleteLoading} />
                            <ApiErrorComponent isError={isError} errorRespnse={error} />
                        </div>
                    </div>
                    <DialogActionsBar>
                        <div className=" spaceBetween">
                            <button className="btn btn-sm btn-default btn-lightGrey" onClick={handleClosedDeleteDialog} autoFocus>
                                Cancel
                            </button>
                            <button
                                className="btn btn-sm btn-danger btn-delete"
                                type="button"
                                style={{
                                    backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
                                    color: isReadOnlyPermission ? 'black' : '#fff'
                                }}
                                onClick={handleDeleteFileClick}
                                disabled={isLoading || isReadOnlyPermission}
                            >
                                Delete
                            </button>
                        </div>
                    </DialogActionsBar>
                </KendoDialog>
            )}
            {openAccDeleteDialog && (
                <KendoDialog className="modalpopup" title={'CONFIRM DELETE'} onClose={handleClosedAccDeleteDialog}>
                    <div>
                        <div style={{ padding: '25px 50px' }}>
                            <div>Are you sure you want to permanently delete?</div>
                            <LoadingIndicator isLoading={accReportDeleteLoading} />
                            <ApiErrorComponent isError={isError} errorRespnse={error} />
                        </div>
                    </div>
                    <DialogActionsBar>
                        <div className=" spaceBetween">
                            <button className="btn btn-sm btn-default btn-lightGrey" onClick={handleClosedAccDeleteDialog} autoFocus>
                                Cancel
                            </button>
                            <button
                                className="btn btn-sm btn-danger btn-delete"
                                type="button"
                                style={{
                                    backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
                                    color: isReadOnlyPermission ? 'black' : '#fff'
                                }}
                                onClick={handleAccDeleteFileClick}
                                disabled={isLoading || isReadOnlyPermission}
                            >
                                Delete
                            </button>
                        </div>
                    </DialogActionsBar>
                </KendoDialog>
            )}
        </>
    );
}